import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { buildSessionParamsFromQuery } from '../services/sessionMapper';

const CREATE_TIMEOUT_MS = 5000;

/**
 * URL cloaking for the legacy query-string entry.
 *
 * On first load, if the URL has query params, this hook creates an opaque
 * session in the background and rewrites the browser URL to
 * `/compliance/{sessionId}` via history.replaceState — so the params disappear
 * from the address bar.
 *
 * GRACEFUL FALLBACK: if session creation fails (network, timeout, server
 * error) the hook does NOT block or break the flow. The app keeps rendering
 * with the original in-memory params; only the URL stays as-is. This guarantees
 * customers can always upload ID / complete KYC even if the Session Manager is
 * down.
 *
 * @param {{ baseUrl: string, token: string, enabled: boolean }} options
 * @returns {{ done: boolean }} done flips true once cloaking finished or was skipped
 */
export const useUrlCloaking = ({ baseUrl, token, enabled = true }) => {
  const [done, setDone] = useState(false);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return undefined;
    ranRef.current = true;

    // Already on a clean session route, or disabled — nothing to cloak.
    if (!enabled) {
      setDone(true);
      return undefined;
    }

    const search = window.location.search;
    if (!search || search.length <= 1) {
      // No query params to hide.
      setDone(true);
      return undefined;
    }

    const params = buildSessionParamsFromQuery(new URLSearchParams(search));
    if (Object.keys(params).length === 0) {
      setDone(true);
      return undefined;
    }

    const cloak = async () => {
      try {
        if (!baseUrl || !/^https:\/\//i.test(baseUrl)) {
          // Non-HTTPS (e.g. local http dev) — skip cloaking, never break.
          setDone(true);
          return;
        }

        const headers = token ? { 'X-API-KEY': token } : {};
        const response = await axios.post(
          `${baseUrl}/mobi/sessions`,
          { params, language: params.lang },
          { headers, timeout: CREATE_TIMEOUT_MS }
        );

        const sessionId = response?.data?.sessionId;
        if (sessionId) {
          // Rewrite URL to the opaque session route without reloading.
          // Note: cloaking is a one-time global action guarded by ranRef, so it
          // is intentionally NOT cancelled by effect cleanup (StrictMode).
          window.history.replaceState(
            window.history.state,
            '',
            `/compliance/${sessionId}`
          );
        }
      } catch (err) {
        // Graceful fallback: keep the current URL/params, do not break.
        // eslint-disable-next-line no-console
        console.warn('URL cloaking skipped:', err.message);
      } finally {
        setDone(true);
      }
    };

    cloak();

    return undefined;
  }, [baseUrl, token, enabled]);

  return { done };
};

export default useUrlCloaking;
